import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Grid,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import katex from 'katex';
import 'katex/dist/katex.min.css';

import { CoordinateGrid } from '../coordinateGrid';
import {
  generateGraphVariations,
  polynomialsToCurveElements,
  equationToLatex,
} from './polynomialGraphUtils';

/**
 * Read-only view for reviewing/grading polynomial graph answers
 *
 * Props:
 * - config: PolynomialGraphConfig
 * - studentAnswer: StudentAnswer
 * - correctAnswer: { correctGraphIndex: number }
 * - isCorrect: boolean
 */
const PolynomialGraphReadOnly = ({
  config,
  studentAnswer,
  correctAnswer,
  isCorrect,
}) => {
  const { parsedPolynomial, interceptType, grid } = config || {};

  // Generate variations
  const variations = useMemo(() => {
    if (!parsedPolynomial) return [];
    try {
      const { variations } = generateGraphVariations(
        parsedPolynomial,
        interceptType,
        config.variationSeed
      );
      return variations;
    } catch (e) {
      console.error('Failed to generate variations:', e);
      return [];
    }
  }, [parsedPolynomial, interceptType, config?.variationSeed]);

  // One shared y-compression across the variation set — per-curve scaling drew
  // curves with the same true y-intercept at different heights.
  const curveElements = useMemo(
    () => polynomialsToCurveElements(variations.map((v) => v.polynomial), grid),
    [variations, grid]
  );

  const studentIntercepts = studentAnswer?.step1?.intercepts || [];
  const studentInterceptLabels = studentAnswer?.step1?.interceptLabels || [];
  const correctIntercepts = useMemo(() => {
    if (!parsedPolynomial) return [];
    if (interceptType === 'x') {
      return parsedPolynomial.factors?.map(f => f.root) || [];
    } else {
      const val = parsedPolynomial.factors?.reduce((acc, f) => acc * Math.pow(-f.root, f.multiplicity), 1) ?? 0;
      return [val];
    }
  }, [parsedPolynomial, interceptType]);
  const usingStudentIntercepts = studentIntercepts.length > 0;
  const displayIntercepts = (usingStudentIntercepts ? studentIntercepts : correctIntercepts).map(
    (n, i) => (usingStudentIntercepts && studentInterceptLabels[i] != null ? studentInterceptLabels[i] : n)
  );
  const studentGraphIndex = studentAnswer?.step2?.selectedGraphIndex;
  const correctGraphIndex = correctAnswer?.correctGraphIndex ?? config?.correctGraphIndex;

  // Guard against missing config
  if (!config || !parsedPolynomial) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Question configuration is missing.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Overall Result - only show when grading context is present */}
      {typeof isCorrect === 'boolean' && (
        <Alert severity={isCorrect ? 'success' : 'error'}>
          {isCorrect ? 'Correct!' : 'Incorrect'}
        </Alert>
      )}

      {/* Equation Display */}
      <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
        <Typography variant="h6" align="center">
          <span dangerouslySetInnerHTML={{
            __html: katex.renderToString(equationToLatex(config.equation), { throwOnError: false })
          }} />
        </Typography>
      </Paper>

      {/* Intercept Answer */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {interceptType === 'x' ? 'X-Intercepts' : 'Y-Intercept'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {displayIntercepts.map((intercept, i) => (
            <Chip
              key={i}
              label={`${interceptType} = ${intercept}`}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      </Paper>

      {/* Graph Selection */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Graph Selection
        </Typography>

        <Grid container spacing={2}>
          {variations.map((variation, index) => {
            const isStudentChoice = index === studentGraphIndex;
            const isCorrectChoice = index === correctGraphIndex;

            return (
              <Grid item xs={6} key={variation.label}>
                <Paper
                  sx={{
                    p: 1,
                    border: isCorrectChoice
                      ? '3px solid green'
                      : isStudentChoice
                      ? '3px solid red'
                      : '1px solid grey',
                    bgcolor: isCorrectChoice
                      ? 'success.light'
                      : isStudentChoice && !isCorrectChoice
                      ? 'error.light'
                      : 'background.paper',
                    position: 'relative',
                  }}
                >
                  {/* Status indicators */}
                  <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                    {isStudentChoice && (
                      <Chip
                        size="small"
                        label="Your answer"
                        color={isCorrectChoice ? 'success' : 'error'}
                        icon={isCorrectChoice ? <CheckCircleIcon /> : <CancelIcon />}
                      />
                    )}
                    {isCorrectChoice && !isStudentChoice && (
                      <Chip
                        size="small"
                        label="Correct"
                        color="success"
                        icon={<CheckCircleIcon />}
                      />
                    )}
                  </Box>

                  <Typography variant="subtitle2" align="center" sx={{ mt: 3 }}>
                    Graph {variation.label}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CoordinateGrid
                      config={grid}
                      elements={[curveElements[index]]}
                      pendingPoints={[]}
                      mode="point"
                      readOnly={true}
                      width={180}
                      height={180}
                    />
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default PolynomialGraphReadOnly;
